import { createRef, useState, useEffect, ReactNode, FunctionComponent } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { LeftArrowNext } from '@tv2/ui-system/icons/next/navigation/LeftArrowNext';
import { RightArrowNext } from '@tv2/ui-system/icons/next/navigation/RightArrowNext';
import styles from './FocusList.module.css';
import Lazy from './Lazy';

import Slideable from './Slideable';
import { WithLink } from '../../types/DeckType';
import WithFocusRing from './WithFocusRing';
import { DeckLabel } from './DeckLabel';

const ButtonWithFocusRing: any = WithFocusRing('button', {
  extraFocusRingClass: styles.arrowFocus,
});

type Meta = { inert: boolean; active: boolean; slide: number };

type FocusListProps<T> = {
  items: T[];
  title?: string | null;
  children: (item: T, meta: Meta, index: number, inGrid: boolean) => ReactNode;
  debounceTime?: number;
  displayTitle?: boolean;
  textColor?: string | null;
  link?: WithLink['link'];
  startIndex?: number;
  className?: any;
  trackingLabel?: string;
  testId?: string;
  layout: 'grid' | 'row' | undefined;
  renderAll?: boolean;
  arrowPosition?: 'top' | 'center';
};

const Resizer = ({ onResize }: any) => {
  useEffect(() => {
    const debouncedHandleResize = debounce(onResize, 100, { maxWait: 1000 });
    window.addEventListener('resize', onResize);
    window.addEventListener('load', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', debouncedHandleResize);
    };
  }, [onResize]);
  return null;
};

const FocusList = <T,>({
  title,
  items,
  link,
  textColor,
  trackingLabel,
  children,
  debounceTime = 200,
  displayTitle = true,
  startIndex = 0,
  testId,
  layout = 'row',
  renderAll = false,
  arrowPosition = 'center',
}: FocusListProps<T>) => {
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [activeIndex, setActiveIndex] = useState<undefined | number>(undefined);

  const gridRef = createRef<HTMLDivElement>();

  const collapse = debounce(() => {
    setActiveIndex(undefined);
  }, debounceTime);
  const handleResize = () => {
    setItemsPerPage(4);
  };
  const expand = debounce((index: number) => {
    setActiveIndex(index);
  }, debounceTime);

  const handleMouseEnter = (index: number) => {
    collapse.cancel();
    expand(index);
  };
  const handleMouseLeave = () => {
    expand.cancel();
    collapse();
  };

  const getItemData = (
    index: number,
    leftIndex: number
  ): [
    {
      className: string;
      key: number;
    },
    Meta,
  ] => {
    const enableHover = false;
    const rightIndex = leftIndex + itemsPerPage - 1;
    const isFocusable = index >= leftIndex && index <= rightIndex;
    const isActive = index === activeIndex;
    const meta = { inert: !isFocusable, active: isActive, slide: 0 };
    const itemProps = {
      className: classNames(styles.item, { [styles.active]: isActive }),
      key: index,
    };

    if (!enableHover) return [itemProps, meta];
    if (isFocusable) {
      Object.assign(itemProps, {
        onMouseEnter: () => handleMouseEnter(index),
        onMouseLeave: handleMouseLeave,
      });
    }

    const shouldTranslate =
      Number.isInteger(activeIndex) && index >= leftIndex - 1 && index <= rightIndex + 1;

    if (!shouldTranslate) return [itemProps, meta];

    meta.slide = index > (activeIndex || 0) ? 0.5 : -0.5;
    if (activeIndex === leftIndex) meta.slide = index > leftIndex ? 1 : 0;
    if (activeIndex === rightIndex) meta.slide = index > rightIndex ? 0 : -1;

    return [itemProps, meta];
  };

  const renderItem = (
    item: T,
    index: number,
    leftIndex: number,
    _renderAll: boolean,
    inGrid: boolean
  ) => {
    const [itemProps, meta] = getItemData(index, leftIndex);
    return index <= itemsPerPage || _renderAll ? (
      <div key={index} className={styles.item}>
        {children(item, meta, index, inGrid)}
      </div>
    ) : (
      <Lazy key={itemProps.key} className={itemProps.className}>
        {children(item, meta, index, inGrid)}
      </Lazy>
    );
  };

  const titleColor = textColor || '#fff';

  if (layout === 'grid') {
    return (
      <div data-testid={testId} data-testtype="deck" className="max-w-full">
        <DeckLabel
          title={title ?? null}
          displayTitle={displayTitle}
          titleColor={titleColor}
          link={link}
        />
        <div
          ref={gridRef}
          style={{ userSelect: 'none' }}
          className={`${styles.scroller} deck pt-4 layout-grid`}
        >
          {items.map((item, i) => renderItem(item, i, 0, renderAll, true))}
        </div>
      </div>
    );
  }

  return (
    <section className={styles.container} data-testid={testId} data-testtype="deck">
      <Resizer onResize={handleResize} />
      <DeckLabel
        title={title ?? null}
        displayTitle={displayTitle}
        titleColor={titleColor}
        link={link}
      />
      <Slideable
        itemCount={items.length}
        itemsPerPage={itemsPerPage}
        gridRef={gridRef}
        startIndex={startIndex}
        trackingLabel={trackingLabel}
      >
        {({ index, leftProps, rightProps }) => (
          <div className={styles.content} role={layout}>
            <ArrowButton
              title="Forrige række"
              arrowPosition={arrowPosition}
              direction="left"
              {...leftProps}
            />
            <div
              ref={gridRef}
              style={{ userSelect: 'none' }}
              className={`${styles.scroller} deck flex flex-row`}
            >
              {items.map((item, i) => renderItem(item, i, index, renderAll, false))}
            </div>
            <ArrowButton
              {...rightProps}
              arrowPosition={arrowPosition}
              direction="right"
              title="Næste række"
            />
          </div>
        )}
      </Slideable>
    </section>
  );
};

const ArrowButton: FunctionComponent<{
  direction: 'left' | 'right';
  title: string;
  disabled: boolean;
  onClick: () => void;
  arrowPosition: 'top' | 'center';
}> = ({ direction, title, disabled, onClick, arrowPosition }) => {
  const [hover, setHover] = useState(false);
  return (
    <ButtonWithFocusRing
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      onFocus={() => setHover(true)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`${styles.arrow} ${arrowPosition === 'top' ? styles.arrowtop : ''} ${
        direction === 'left' ? styles.left : styles.right
      }`}
    >
      <span
        className={classNames(styles.arrowCircle, {
          'bg-grey-100 text-black duration-200': hover,
          'bg-midnight/60': !hover,
        })}
      >
        {direction === 'left' ? (
          <LeftArrowNext className={classNames({ invert: !hover })} height={24} width={24} />
        ) : (
          <RightArrowNext className={classNames({ invert: !hover })} height={24} width={24} />
        )}
      </span>{' '}
    </ButtonWithFocusRing>
  );
};

export default FocusList;
